import { useEffect, useMemo, useRef, useState } from 'react';

import type { Interpolation } from '@emotion/react';
import { Box, CircularProgress, IconButton, useTheme } from '@mui/material';
import { sanitizeHtml } from '@realadvisor/html-sanitizer';
import { EllipsisIcon } from 'lucide-react';

import { findQuotes, mediaCss } from '@modules/_shared/components/email-editor';
import { fetchEmailBody } from '@modules/_shared/utils/fetchEmailBody';

type UnfoldedEmailMessageProps = {
  emailMessageId: string;
  createdBy: string;
  fallback?: React.ReactNode;
  onLoadingChange?: (loading: boolean) => void;
};

const Preview = ({
  styles,
  children,
}: {
  styles: Interpolation<unknown>;
  children: string | null;
}) => {
  const theme = useTheme();
  const { text } = theme;
  const palette = (theme.vars ?? theme).palette;
  return (
    <Box
      sx={{
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        wordBreak: 'break-word',
        // The served body keeps its layout (centered columns, fixed-width
        // tables); this sink is the page's DOM, not an iframe, so it confines
        // width itself.
        overflowX: 'auto',
        '& table': { maxWidth: '100%' },
        '& img': { maxWidth: '100%', height: 'auto' },
        '& > *:first-of-type': {
          marginTop: 0,
        },
        '& *:last-child': {
          marginBottom: 0,
        },
        '& a': {
          color: palette.primary.main,
          textDecoration: 'none',
        },
        '& p': { m: 0, mb: 0.5 },
        '& p:last-child': { mb: 0 },
        '& ul': { m: 0, pl: 2.5, listStyleType: 'disc' },
        '& ul ul': { listStyleType: 'circle' },
        '& ol': { m: 0, pl: 2.5 },
        '& li': { mb: 0.25 },
        '& li:last-child': { mb: 0 },
        '& strong': { fontWeight: text.font.bold },
      }}
      css={styles}
      // Sanitized a second time AT the sink: the quote split re-serializes
      // the DOM, and a serializer leaves `<` unescaped inside attribute values
      // where a raw-text element (`<noscript>`) reads differently with
      // scripting on than in the inert parse (mutation XSS). Re-tokenizing
      // the serialized halves re-encodes those values; the sanitizer is
      // idempotent on its own output.
      dangerouslySetInnerHTML={{
        __html: children != null ? sanitizeHtml(children) : '',
      }}
    />
  );
};

export const UnfoldedEmailMessage = ({
  emailMessageId,
  createdBy,
  fallback,
  onLoadingChange,
}: UnfoldedEmailMessageProps) => {
  const theme = useTheme();
  const palette = (theme.vars ?? theme).palette;
  const [body, setBody] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showQuoted, setShowQuoted] = useState(false);

  const onLoadingChangeRef = useRef(onLoadingChange);
  onLoadingChangeRef.current = onLoadingChange;

  useEffect(() => {
    let cancelled = false;
    const setLoadingState = (value: boolean) => {
      setLoading(value);
      onLoadingChangeRef.current?.(value);
    };
    setLoadingState(true);
    fetchEmailBody(emailMessageId, createdBy)
      .then(fetchedBody => {
        if (!cancelled) {
          setBody(fetchedBody);
          setLoadingState(false);
        }
      })
      .catch((error: unknown) => {
        console.error('email body fetch failed', error);
        if (!cancelled) {
          setLoadingState(false);
        }
      });
    return () => {
      cancelled = true;
      onLoadingChangeRef.current?.(false);
    };
  }, [emailMessageId, createdBy]);

  // Sanitized BEFORE the split (the split parses the HTML into a DOM, inert
  // but still a parse of untrusted markup) AND again at the sink, see Preview.
  // Once per body: the sanitizer and the parse are not free on a long mail,
  // and the quote toggle re-renders.
  const { message, quotes } = useMemo(
    () => findQuotes(sanitizeHtml(body ?? '')),
    [body],
  );

  if (loading) {
    return fallback ?? <CircularProgress size={20} />;
  }

  return (
    <>
      <Preview styles={mediaCss}>{message}</Preview>
      {quotes != null && (
        <>
          <IconButton
            onClick={() => setShowQuoted(show => !show)}
            sx={{ backgroundColor: palette.grey[300] }}
          >
            <EllipsisIcon size={20} />
          </IconButton>
          {showQuoted && <Preview styles={{}}>{quotes}</Preview>}
        </>
      )}
    </>
  );
};
